import React from 'react';
import { useDispatch, connect } from 'react-redux';
import PropTypes from 'prop-types';

import Modal from '../Modal';
import Button from '../Button';
import Icon from '../Icon';
import SearchResult from '../SearchResult/SearchResult';
import BrokenHeart from '../../svg/icons/ui-favorite.svg';
import Close from '../../../../consumer_web/app/modules/svg/dynamic/close.svg';
import Divider from '../Divider';
import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';
import {
  MODAL_MODES,
  setMultiStepModalMode,
  setMultiStepModalOpenState,
  setMultiStepModalVenueId,
  setMultiStepModalVenueContext,
} from '../../reducers/favoriteSlice';

import './NotifyVenueGroupModal.scss';

const NotifyVenueGroupModal = ({
  $rootScope,
  Analytics,
  isMobile,
  isOpen,
  notifyVenueName,
  onClose,
  venues,
  isMultiStepModalOpen,
}) => {
  const dispatch = useDispatch();

  const handleFavoriteClick = (response) => {
    const { venue_id: venueId, venue } = response;

    dispatch(setMultiStepModalVenueContext({ name: venue?.name, city: venue?.location?.locality, context: 'Venue Group list' }));
    dispatch(setMultiStepModalVenueId(venueId));
    dispatch(setMultiStepModalMode(MODAL_MODES.ADD_TO_LIST));
    dispatch(setMultiStepModalOpenState(true));
  };

  const handleModalClose = () => {
    onClose();
  };

  const getSubtitle = () => {
    if (venues.length === 1) {
      return (
        getTranslation({
          translationKey: 'notify_passed_one_venue',
          translationValues: { notifyVenue: notifyVenueName },
          fallback:
            `You can set another Notify for ${notifyVenueName} and try again, or check out their sister restaurant.`,
        })
      );
    }

    return (
      getTranslation({
        translationKey: 'notify_passed_multiple_venues',
        translationValues: { notifyVenue: notifyVenueName },
        fallback:
          `You can set another Notify for ${notifyVenueName} and try again, or check out their sister restaurants.`,
      })
    );
  };

  const getHeaderContent = () => (
    <div className="NotifyVenueGroupModal__title-wrapper">
      <div className="NotifyVenueGroupModal__title-container">
        <TranslationProvider translationKey="notify_passed_sorry">
          <div className="NotifyVenueGroupModal__title">
            Sorry it didn&apos;t work out
          </div>
        </TranslationProvider>
        <Icon component={BrokenHeart} name="broken-heart" />
      </div>

      <TranslationProvider translationKey="notify_passed_try_again">
        <div className="NotifyVenueGroupModal__subtitle">
          {getSubtitle()}
        </div>
      </TranslationProvider>

      <hr className="NotifyVenueGroupModal__hr" />
    </div>
  );

  const venueCardList = () => venues.map((venue, index) => {
    const renderDivider = () => {
      if (index !== venues.length - 1) {
        return (
          <Divider marginHorizontal={10} />
        );
      }

      return null;
    };

    return (
      <div key={venue.id.resy}>
        <SearchResult
          $rootScope={$rootScope}
          canFavorite
          key={venue.id}
          venue={venue}
          showSlots={false}
          isMobile={isMobile}
          onFavoriteClick={handleFavoriteClick}
          event={Analytics.event}
          compact
          showBorder={false}
          urlSlug={venue.location.url_slug}
          showBadges={false}
        />

        {renderDivider()}
      </div>
    );
  });

  return (
    <Modal
      hasFooter
      hasSecondaryCTA={false}
      isOpen={isOpen && !isMultiStepModalOpen}
      onRequestClose={handleModalClose}
      primaryCTAText={getTranslation({ translationKey: 'ok', fallback: 'OK' })}
      primaryCTAVariant="primary"
      primaryCTAAdditionalProps={{
        onClick: handleModalClose,
        'data-test-id': 'notify-window-passed-primary-cta',
      }}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
      styles={{
        content: {
          maxWidth: '400px',
          inset: '15vh 0 auto',
        },
        overlay: {
          zIndex: '6',
          overflowY: 'auto',
        },
      }}
    >
      <div className="NotifyVenueGroupModal">
        <div className="NotifyVenueGroupModal__header">
          <Button
            onClick={handleModalClose}
            variant="no-style"
            className="Button__close"
            aria-label={getTranslation({
              translationKey: 'close',
              fallback: 'Close',
            })}
            data-test-id="notify-window-passed-button-close"
          >
            <Close />
          </Button>
        </div>

        {getHeaderContent()}

        <div className="NotifyVenueGroupModal__card-container">
          <TranslationProvider translationKey="notify_passed_group" translationValues={{ notifyVenue: notifyVenueName }}>
            <div className="NotifyVenueGroupModal__content-header">
              {`From the team behind ${notifyVenueName}...`}
            </div>
          </TranslationProvider>

          {venueCardList()}
        </div>
      </div>
    </Modal>
  );
};

export const NotifyVenueGroupModalPropTypes = {
  $rootScope: PropTypes.shape({
    $broadcast: PropTypes.func.isRequired,
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func.isRequired,
  }).isRequired,
  isMobile: PropTypes.bool,
  isMultiStepModalOpen: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  notifyVenueName: PropTypes.string.isRequired,
  venues: PropTypes.arrayOf(
    PropTypes.shape({})
  ),
};

NotifyVenueGroupModal.propTypes = NotifyVenueGroupModalPropTypes;

NotifyVenueGroupModal.defaultProps = {
  venues: [],
  isMobile: false,
  isOpen: false,
};

const mapStateToProps = ({ favorite }) => ({
  isMultiStepModalOpen: favorite.isMultiStepModalOpen,
});

NotifyVenueGroupModal.displayName = 'NotifyVenueGroupModal';
export default connect(mapStateToProps)(NotifyVenueGroupModal);
